/**
 * EventBuilder - Type-safe builder for CRM events.
 *
 * Handles event construction with proper sequencing, timestamps,
 * and idempotency keys.
 */

import type { BaseEvent, EventConfig, EventTypeName } from './types';

/**
 * Helper to generate UUID v4.
 */
function generateUUID(): string {
  return crypto.randomUUID();
}

/**
 * Helper to get ISO 8601 timestamp.
 */
function getCurrentTimestamp(): string {
  return new Date().toISOString();
}

/**
 * EventBuilder class for constructing type-safe CRM events.
 */
export class EventBuilder {
  private events: BaseEvent[] = [];
  private config: { sourceType: string; sourceId: string; startSeq?: number };
  /** Track sequence numbers per entity for proper event ordering */
  private seqByEntity: Map<string, number> = new Map();
  private defaultStartSeq: number;

  constructor(config: EventConfig) {
    this.config = {
      sourceType: config.sourceType || 'ahoy_user',
      sourceId: config.sourceId,
      startSeq: config.startSeq,
    };
    // Default start sequence: provided value + 1 (for updates) or 1 (for new objects)
    this.defaultStartSeq =
      config.startSeq !== undefined ? config.startSeq + 1 : 1;
  }

  /**
   * Get the next sequence number for a entity.
   * Each entity maintains its own sequence counter.
   */
  private getNextSeq(entityId: string): number {
    const currentSeq = this.seqByEntity.get(entityId) ?? this.defaultStartSeq;
    this.seqByEntity.set(entityId, currentSeq + 1);
    return currentSeq;
  }

  /**
   * Seed the per-entity sequence counter so `entityId`'s next event uses `seq`
   * (subsequent events for that entity increment from there). Call BEFORE
   * emitting that entity's first event.
   *
   * The builder's default start sequence is derived from the primary entity's
   * last persisted seq, which is > 1 whenever that entity already exists (e.g.
   * the matched-contact upsert path folds drafts onto a builder started at the
   * existing contact's seq). A brand-new entity's first event must be seq 1
   * regardless — folding it onto such a builder without seeding would inherit
   * the parent's continuation seq, and the backend would reject the new
   * entity's objectCreated as a sequence conflict. Used by `appendDraftEvents`
   * so nested-create drafts (always new, client-minted entities) always start
   * at seq 1.
   */
  seedEntitySeq(entityId: string, seq: number): this {
    this.seqByEntity.set(entityId, seq);
    return this;
  }

  /**
   * Build a complete event with all required fields.
   */
  private buildEvent(
    entityId: string,
    entityTypeName: string,
    eventTypeName: EventTypeName,
    payload: Record<string, unknown>
  ): BaseEvent {
    return {
      entity_id: entityId,
      entity_type_name: entityTypeName,
      event_type_name: eventTypeName,
      seq: this.getNextSeq(entityId),
      idempotency_key: generateUUID(),
      occurred_at: getCurrentTimestamp(),
      payload,
      source_type_name: this.config.sourceType,
      source_id: this.config.sourceId,
    };
  }

  /**
   * Add an object_created event.
   */
  objectCreated(
    entityId: string,
    entityTypeName: string,
    properties: Record<string, unknown>,
    extra?: Record<string, unknown>
  ): this {
    const payload: Record<string, unknown> = { properties };
    if (extra) {
      Object.assign(payload, extra);
    }
    this.events.push(
      this.buildEvent(entityId, entityTypeName, 'ahoy_object_created', payload)
    );
    return this;
  }

  /**
   * Add a property_updated event (single property).
   *
   * Note: For updating multiple properties at once, use propertiesUpdated() instead.
   */
  propertyUpdated(
    entityId: string,
    entityTypeName: string,
    propertyTypeName: string,
    propertyValue: unknown
  ): this {
    this.events.push(
      this.buildEvent(entityId, entityTypeName, 'ahoy_property_updated', {
        properties: {
          [propertyTypeName]: propertyValue,
        },
      })
    );
    return this;
  }

  /**
   * Add a property_updated event with multiple properties.
   *
   * Use this to bundle multiple property updates into a single event,
   * which creates a cleaner entity and better performance.
   *
   * @param entityId - Entity identifier
   * @param entityTypeName - Entity type name
   * @param properties - Object mapping property names to values
   */
  propertiesUpdated(
    entityId: string,
    entityTypeName: string,
    properties: Record<string, unknown>
  ): this {
    this.events.push(
      this.buildEvent(entityId, entityTypeName, 'ahoy_property_updated', {
        properties,
      })
    );
    return this;
  }

  /**
   * Add an association_added event.
   */
  associationAdded(
    entityId: string,
    entityTypeName: string,
    associationTypeName: string,
    toEntityId: string
  ): this {
    this.events.push(
      this.buildEvent(entityId, entityTypeName, 'ahoy_association_added', {
        association_type_name: associationTypeName,
        target_entity_id: toEntityId,
      })
    );
    return this;
  }

  /**
   * Add an association_removed event.
   */
  associationRemoved(
    entityId: string,
    entityTypeName: string,
    associationTypeName: string,
    toEntityId: string
  ): this {
    this.events.push(
      this.buildEvent(entityId, entityTypeName, 'ahoy_association_removed', {
        association_type_name: associationTypeName,
        target_entity_id: toEntityId,
      })
    );
    return this;
  }

  /**
   * Add an object_deleted event.
   */
  objectDeleted(entityId: string, entityTypeName: string): this {
    this.events.push(
      this.buildEvent(entityId, entityTypeName, 'ahoy_object_deleted', {})
    );
    return this;
  }

  /**
   * Add an object_merged event. Source entity (entityId) is soft-deleted;
   * canonical entity receives all associations via cascade.
   *
   * @param propertyResolutions - Optional map of property name → resolved value.
   *   When provided, the backend's `MergePropertyCascade` applies these
   *   resolutions on the canonical entity after the merge releases
   *   unique-value claims from the source entity. Omitted or empty maps
   *   produce a payload identical to callers that pass no resolutions.
   */
  objectMerged(
    entityId: string,
    entityTypeName: string,
    canonicalEntityId: string,
    propertyResolutions?: Record<string, unknown>
  ): this {
    const payload: Record<string, unknown> = {
      canonical_entity_id: canonicalEntityId,
    };
    if (propertyResolutions && Object.keys(propertyResolutions).length > 0) {
      payload.property_resolutions = propertyResolutions;
    }
    this.events.push(
      this.buildEvent(entityId, entityTypeName, 'ahoy_object_merged', payload)
    );
    return this;
  }

  /**
   * Get all built events.
   */
  build(): BaseEvent[] {
    return this.events;
  }

  /**
   * Reset the builder for reuse.
   */
  reset(): this {
    this.events = [];
    this.seqByEntity.clear();
    return this;
  }
}

/**
 * Factory function for creating event builders.
 * @param config - Required configuration including sourceId (user ID for ahoy_user events)
 */
export function createEventBuilder(config: EventConfig): EventBuilder {
  return new EventBuilder(config);
}
